/**
 * "See it on paper": the letter being written, laid out on the chosen paper,
 * sheet by sheet — the same layout the reader will get. Opened from the
 * composer; nothing is sent from here.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X } from 'lucide-react';
import type { Stationery } from '../letters/api';
import { LetterPaper } from './LetterPaper';

export function PaperPreviewButton({ paper, text }: { paper: Stationery | null; text: string }) {
  const [open, setOpen] = useState(false);
  if (!paper) return null;
  return (
    <>
      <button type="button" className="linkish paper-preview-link" onClick={() => setOpen(true)}>
        <FileText size={13} aria-hidden="true" />
        <span>See it on {paper.name}</span>
      </button>
      {open && <PaperPreview paper={paper} text={text} onClose={() => setOpen(false)} />}
    </>
  );
}

function PaperPreview({ paper, text, onClose }: { paper: Stationery; text: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="sheet-backdrop gift-book-backdrop" onClick={onClose}>
      <div
        className="sheet gift-book paper-preview"
        role="dialog"
        aria-modal="true"
        aria-label={`Your letter on ${paper.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet__grip" aria-hidden="true" />
        <button type="button" className="stamp-picker__close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <h2 className="gift-book__title">On {paper.name}</h2>
        <LetterPaper paper={paper} text={text.trim() || 'Your letter will sit here, on this paper.'} />
      </div>
    </div>,
    document.body,
  );
}
